import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Sparkle, ArrowRight, CheckCircle, Warning, Buildings, Users, CurrencyInr,
  CalendarStar, ClipboardText, FileText, ArrowsClockwise, ArrowLeft, FloppyDisk,
  X, Circle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const INDUSTRIES = [
  "IT / SaaS", "Manufacturing", "Retail / E-commerce", "BFSI / Fintech",
  "Healthcare / Pharma", "Education / EdTech", "Logistics & Supply Chain",
  "Consulting & Professional Services", "Media & Advertising", "Other",
];
const BUSINESS_TYPES = ["services", "product", "manufacturing", "retail", "trading", "nonprofit"];

const STEPS = [
  { key: "intro", label: "Company profile" },
  { key: "review", label: "Review AI suggestions" },
  { key: "apply", label: "Apply" },
];

export default function SetupWizardPanel({ params }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({
    industry: "IT / SaaS", country: "India", state: "", headcount: 50,
    business_type: "services", focus: "",
  });
  const [bundle, setBundle] = useState(null);
  const [accepted, setAccepted] = useState({
    policies: {}, grades: true, salary: true, holidays: true, required_docs: true,
  });
  const [applyResult, setApplyResult] = useState(null);

  const generate = async () => {
    if (!profile.industry) { toast.error("Pick an industry"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/setup-wizard/generate", { ...profile, ...params }, { timeout: 90000 });
      setBundle(data);
      // Accept all policies by default
      const policyAccept = {};
      (data.policies || []).forEach((p) => { policyAccept[p.kind] = true; });
      setAccepted({ policies: policyAccept, grades: true, salary: true, holidays: true, required_docs: true });
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const regeneratePolicies = async () => {
    if (!profile.industry) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/setup-wizard/generate", { ...profile, ...params }, { timeout: 90000 });
      setBundle((b) => ({ ...b, policies: data.policies }));
      const policyAccept = {};
      (data.policies || []).forEach((p) => { policyAccept[p.kind] = true; });
      setAccepted((a) => ({ ...a, policies: policyAccept }));
      toast.success("Regenerated");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const editPolicySummary = (kind, next) => {
    setBundle((b) => ({ ...b, policies: b.policies.map((p) => p.kind === kind ? { ...p, summary: next } : p) }));
  };

  const apply = async () => {
    setBusy(true);
    try {
      const payload = {
        ...params,
        apply_policies: Object.values(accepted.policies || {}).some(Boolean),
        policies: (bundle.policies || []).map((p) => ({ ...p, accepted: !!accepted.policies[p.kind] })),
        apply_grades: accepted.grades,
        grades: bundle.grade_structure || [],
        bands: bundle.band_structure || [],
        apply_salary: accepted.salary,
        salary_split: bundle.salary_split ? {
          basic_percent: bundle.salary_split.basic_percent,
          hra_percent: bundle.salary_split.hra_percent,
          special_percent: bundle.salary_split.special_percent,
          pf_applicable: bundle.salary_split.pf_applicable,
          esic_applicable: bundle.salary_split.esic_applicable,
          pt_applicable: bundle.salary_split.pt_applicable,
        } : null,
        apply_holidays: accepted.holidays,
        holidays: bundle.holidays || [],
        apply_required_docs: accepted.required_docs,
        required_docs: bundle.required_docs || [],
      };
      const { data } = await api.post("/admin/setup-wizard/apply", payload);
      setApplyResult(data.applied);
      setStep(2);
      toast.success("Setup applied. Sections merged into your tenant configuration.");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 max-w-6xl">
      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${step >= i ? "bg-adb-navy text-white" : "bg-zinc-200 text-zinc-500"}`}>{i + 1}</div>
            <div className={`text-xs ${step >= i ? "font-semibold text-adb-navy" : "text-zinc-500"}`}>{s.label}</div>
            {i < STEPS.length - 1 && <ArrowRight size={14} className="text-zinc-300" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="border border-zinc-200 bg-white rounded-sm p-6 space-y-4" data-testid="wizard-step-profile">
          <div>
            <div className="font-display font-bold text-xl flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-adb-green" /> Tell us about your company</div>
            <div className="text-sm text-zinc-500">We'll draft policies, grades &amp; bands, salary split, holidays and required docs tuned to your industry.</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Industry <span className="text-red-500">*</span></Label>
              <Select value={profile.industry} onValueChange={(v) => setProfile({ ...profile, industry: v })}>
                <SelectTrigger className="rounded-sm" data-testid="wizard-industry"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Business type</Label>
              <Select value={profile.business_type} onValueChange={(v) => setProfile({ ...profile, business_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="wizard-biztype"><SelectValue /></SelectTrigger>
                <SelectContent>{BUSINESS_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Country</Label>
              <Input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} className="rounded-sm" data-testid="wizard-country" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">State (optional)</Label>
              <Input value={profile.state} onChange={(e) => setProfile({ ...profile, state: e.target.value })} className="rounded-sm" placeholder="Karnataka, Maharashtra…" data-testid="wizard-state" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Approximate headcount</Label>
              <Input type="number" min="1" value={profile.headcount} onChange={(e) => setProfile({ ...profile, headcount: parseInt(e.target.value) || 0 })} className="rounded-sm font-mono-plex" data-testid="wizard-headcount" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Additional focus (optional)</Label>
              <Textarea rows={2} value={profile.focus} onChange={(e) => setProfile({ ...profile, focus: e.target.value })} placeholder="e.g. Hybrid work model, high-growth stage, regulated data, remote-first…" className="rounded-sm" data-testid="wizard-focus" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={generate} disabled={busy} className="bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold rounded-sm" data-testid="wizard-generate">
              <Sparkle size={14} className="mr-1.5" /> {busy ? "Drafting (10-20s)…" : "Generate suggestions"}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && bundle && (
        <div className="space-y-4" data-testid="wizard-step-review">
          {bundle.summary && (
            <div className="border border-adb-navy/20 bg-adb-navy/5 rounded-sm p-3 text-sm text-adb-navy">
              <span className="font-semibold">AI notes: </span>{bundle.summary}
            </div>
          )}
          {bundle.ai_error && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm p-2 text-xs text-amber-800">
              <Warning size={12} className="inline mr-1" /> AI policy call failed — using fallback templates. You can regenerate below.
            </div>
          )}

          {/* Policies */}
          <ReviewCard
            title="Policies"
            subtitle={`${(bundle.policies || []).length} draft policies · click Regenerate for new suggestions`}
            icon={<FileText size={16} />}
            action={
              <Button size="sm" variant="outline" onClick={regeneratePolicies} disabled={busy} className="rounded-sm" data-testid="wizard-regen-policies">
                <ArrowsClockwise size={12} className="mr-1" /> {busy ? "…" : "Regenerate"}
              </Button>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(bundle.policies || []).map((p) => {
                const on = !!accepted.policies[p.kind];
                return (
                  <div key={p.kind} className={`border rounded-sm p-3 transition-colors ${on ? "border-adb-green/50 bg-adb-green/5" : "border-zinc-200 bg-white opacity-60"}`} data-testid={`wizard-policy-${p.kind}`}>
                    <div className="flex items-start justify-between gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={(e) => setAccepted({ ...accepted, policies: { ...accepted.policies, [p.kind]: e.target.checked } })}
                          data-testid={`wizard-policy-check-${p.kind}`}
                        />
                        <div>
                          <div className="text-sm font-semibold">{p.title}</div>
                          <Badge variant="outline" className="rounded-sm text-[9px] font-mono-plex mt-0.5">{p.kind}</Badge>
                        </div>
                      </label>
                    </div>
                    <Textarea
                      value={p.summary || ""}
                      onChange={(e) => editPolicySummary(p.kind, e.target.value)}
                      rows={3}
                      className="mt-2 rounded-sm text-xs"
                      data-testid={`wizard-policy-summary-${p.kind}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              Note: policies are saved as summaries. Use <b>Use Zenith360AI</b> on the Policies admin tab afterwards to elaborate any policy into a full India-first document.
            </div>
          </ReviewCard>

          {/* Grades & bands */}
          <ReviewCard
            title="Grades & bands"
            subtitle={`${(bundle.grade_structure || []).length} grades in ${(bundle.band_structure || []).length} bands`}
            icon={<Users size={16} />}
            action={
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={accepted.grades} onChange={(e) => setAccepted({ ...accepted, grades: e.target.checked })} data-testid="wizard-grades-check" />
                Include in apply
              </label>
            }
          >
            <div className="overflow-x-auto">
              <table className="hr-table">
                <thead><tr><th>Grade</th><th>Level</th><th>Band</th><th>Title example</th><th>CTC (LPA)</th></tr></thead>
                <tbody>
                  {(bundle.grade_structure || []).map((g, i) => (
                    <tr key={i}>
                      <td className="font-mono-plex text-xs">{g.grade}</td>
                      <td className="font-mono-plex text-xs">{g.level}</td>
                      <td className="text-sm"><Badge variant="outline" className="rounded-sm text-[10px]">{g.band}</Badge></td>
                      <td className="text-xs text-zinc-500">{g.title_example || "—"}</td>
                      <td className="font-mono-plex text-xs">{g.min_ctc_lpa ?? "—"} – {g.max_ctc_lpa ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ReviewCard>

          {/* Salary split */}
          {bundle.salary_split && (
            <ReviewCard
              title="Salary split"
              subtitle="Basic / HRA / Special + statutory applicability"
              icon={<CurrencyInr size={16} />}
              action={
                <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={accepted.salary} onChange={(e) => setAccepted({ ...accepted, salary: e.target.checked })} data-testid="wizard-salary-check" />
                  Include in apply
                </label>
              }
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SalaryPct label="Basic %" value={bundle.salary_split.basic_percent} />
                <SalaryPct label="HRA %" value={bundle.salary_split.hra_percent} />
                <SalaryPct label="Special %" value={bundle.salary_split.special_percent} />
                <SalaryPct label="Total" value={(bundle.salary_split.basic_percent || 0) + (bundle.salary_split.hra_percent || 0) + (bundle.salary_split.special_percent || 0)} bold />
              </div>
              <div className="flex gap-1.5 flex-wrap mt-3">
                <Badge variant="outline" className={`rounded-sm ${bundle.salary_split.pf_applicable ? "bg-adb-green/10 border-adb-green/40 text-adb-navy" : "bg-zinc-50 border-zinc-200 text-zinc-500"}`}>PF {bundle.salary_split.pf_applicable ? "ON" : "OFF"}</Badge>
                <Badge variant="outline" className={`rounded-sm ${bundle.salary_split.esic_applicable ? "bg-adb-green/10 border-adb-green/40 text-adb-navy" : "bg-zinc-50 border-zinc-200 text-zinc-500"}`}>ESIC {bundle.salary_split.esic_applicable ? "ON" : "OFF"}</Badge>
                <Badge variant="outline" className={`rounded-sm ${bundle.salary_split.pt_applicable ? "bg-adb-green/10 border-adb-green/40 text-adb-navy" : "bg-zinc-50 border-zinc-200 text-zinc-500"}`}>PT {bundle.salary_split.pt_applicable ? "ON" : "OFF"}</Badge>
              </div>
              {bundle.salary_split.rationale && <div className="text-xs text-zinc-500 mt-2 italic">{bundle.salary_split.rationale}</div>}
              {(bundle.salary_split.common_components || []).length > 0 && (
                <div className="text-xs text-zinc-600 mt-2"><b>Common components:</b> {(bundle.salary_split.common_components || []).join(", ")}</div>
              )}
            </ReviewCard>
          )}

          {/* Holidays */}
          <ReviewCard
            title="Holiday calendar"
            subtitle={`${(bundle.holidays || []).length} holidays proposed for the year`}
            icon={<CalendarStar size={16} />}
            action={
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={accepted.holidays} onChange={(e) => setAccepted({ ...accepted, holidays: e.target.checked })} data-testid="wizard-holidays-check" />
                Include in apply
              </label>
            }
          >
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
              {(bundle.holidays || []).map((h, i) => (
                <div key={i} className="flex items-center gap-2 border border-zinc-200 rounded-sm px-2 py-1.5">
                  <span className="font-mono-plex text-[10px] text-zinc-500 w-20">{h.date}</span>
                  <span className="flex-1 truncate">{h.name}</span>
                  <Badge variant="outline" className="rounded-sm text-[9px] font-mono-plex">{h.kind}</Badge>
                </div>
              ))}
            </div>
          </ReviewCard>

          {/* Required docs */}
          <ReviewCard
            title="Required onboarding documents"
            subtitle={`${(bundle.required_docs || []).length} documents joiners must upload`}
            icon={<ClipboardText size={16} />}
            action={
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input type="checkbox" checked={accepted.required_docs} onChange={(e) => setAccepted({ ...accepted, required_docs: e.target.checked })} data-testid="wizard-docs-check" />
                Include in apply
              </label>
            }
          >
            <div className="flex flex-wrap gap-1.5">
              {(bundle.required_docs || []).map((d) => (
                <Badge key={d} variant="outline" className="rounded-sm bg-adb-navy/5 border-adb-navy/20 text-adb-navy font-mono-plex text-[10px]">{d.replace(/_/g, " ")}</Badge>
              ))}
            </div>
          </ReviewCard>

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(0)} className="rounded-sm" data-testid="wizard-back">
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
            <Button onClick={apply} disabled={busy} className="bg-adb-navy text-white hover:bg-adb-navy-800 rounded-sm" data-testid="wizard-apply">
              <FloppyDisk size={14} className="mr-1.5" /> {busy ? "Applying…" : "Apply selected sections"}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && applyResult && (
        <div className="border border-adb-green bg-adb-green/5 rounded-sm p-8 text-center space-y-4" data-testid="wizard-step-done">
          <CheckCircle size={44} weight="fill" className="mx-auto text-adb-green" />
          <div className="font-display text-2xl font-bold">Setup complete!</div>
          <div className="text-sm text-zinc-600">Your company configuration has been saved.</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 max-w-xl mx-auto text-xs">
            <ResultCell label="Policies" n={applyResult.policies} />
            <ResultCell label="Grades" n={applyResult.grades} />
            <ResultCell label="Bands" n={applyResult.bands} />
            <ResultCell label="Salary" n={applyResult.salary ? "✓" : "—"} />
            <ResultCell label="Holidays" n={applyResult.holidays} />
            <ResultCell label="Docs" n={applyResult.required_docs} />
          </div>
          <div className="pt-2">
            <Button variant="outline" onClick={() => { setStep(0); setBundle(null); setApplyResult(null); }} className="rounded-sm" data-testid="wizard-restart">
              Start again with a different profile
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewCard({ title, subtitle, icon, action, children }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <div className="text-adb-navy">{icon}</div>
        <div className="flex-1">
          <div className="font-display font-bold text-sm">{title}</div>
          <div className="text-xs text-zinc-500">{subtitle}</div>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function SalaryPct({ label, value, bold }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-2.5 bg-zinc-50">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-0.5 font-mono-plex ${bold ? "text-xl font-bold text-adb-navy" : "text-lg"}`}>{value ?? "—"}%</div>
    </div>
  );
}

function ResultCell({ label, n }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-2 bg-white">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono-plex text-xl font-bold text-adb-navy">{n}</div>
    </div>
  );
}
